/*
 * THE ADDRESS THE APP ANSWERS TO — spelled once, for the two codebases that write one.
 *
 * The app reflects its view into the address bar and restores it on arrival (`web/src/nav/
 * permalink.ts`). The pipeline writes 52,406 of those addresses into the static pages, and every
 * one of them is a call to action: they are the only links in the library that convert a reader
 * into a user. Both halves were spelling the format by hand.
 *
 * THAT HAS ALREADY COST THIS PROJECT ONE FEATURE, IN FULL. All 36 episode pages carried
 * `?compare=<bare id>` where the app requires `story:` in front of it, so the highest-intent click
 * in the library opened the front door with the request still in the address bar — for the entire
 * life of the feature. `deeplink.spec.ts` built its own copy of the format by hand and passed every
 * run. `schema/compare-link.ts` is the answer to that one, and this is the answer to the rest of
 * them: `?node=` was spelled in five places, `?panel=` in four, `?passage=` in three, none of them
 * shared with the app that reads them.
 *
 * A reader of this file wanting the compare VALUE (`story:…`, `told:a~b`) wants `compare-link.ts`;
 * this module is about the URL those values sit in.
 */

/**
 * Every dimension of the app's view that a URL can carry. All optional: a bare URL is the map.
 *
 * Declared here rather than in the app because the pages build URLs against it. The app's own
 * `readAppUrl`/`writeAppUrl` read and write these same names — see `web/src/nav/permalink.ts`.
 */
export interface AppUrlState {
  /** The open figure drawer, by node id. */
  node?: string;
  /** The expanded reader, by the citation of the verse it is centred on (or a contested chapter ref). */
  passage?: string;
  /** A side-by-side comparison — build the value with `compareValue` in `./compare-link.js`. */
  compare?: string;
  /** A full panel: `sources` or `disputed`. */
  panel?: string;
  /** The discussion drawer, by its scope key: a node id, or `verse:<citation>`. */
  discuss?: string;
  /** A leader's Hall, by its slug — the address they put in a YouTube description. */
  hall?: string;
  /** What happened on the way back from Google. Read on arrival, never written — see the app. */
  signin?: string;
}

/**
 * The query parameters the app owns, in the order they are written.
 *
 * Every other parameter in a URL is PRESERVED by the app rather than stripped, so a debugging flag
 * survives opening a view. That is the app's business; this list is what both halves must agree on.
 */
export const APP_URL_PARAMS: readonly (keyof AppUrlState)[] =
  ['node', 'passage', 'compare', 'panel', 'discuss', 'hall', 'signin'];

/**
 * An absolute app URL for one view — what a static page links to.
 *
 * `origin` is the site's origin with no trailing slash (`https://versesverse.com`); the result is
 * `${origin}/?node=abraham`. Values are encoded, which is not decoration: a passage is addressed by
 * its CITATION, so `Isaiah 53` and `1 Chronicles 20` carry spaces, and a hand-written template that
 * forgets this produces a link that resolves and opens nothing.
 *
 * Empty and absent are the same thing and both are dropped, for the reason the app drops them on
 * the way in: `?node=` with nothing after it is what a truncated link looks like, and it should
 * land on the map rather than on a lookup for "".
 */
export function appUrl(origin: string, state: AppUrlState): string {
  const base = origin.replace(/\/+$/, '');
  const parts: string[] = [];
  for (const key of APP_URL_PARAMS) {
    const value = state[key];
    if (value === undefined || value.trim() === '') continue;
    /*
     * `encodeURIComponent`, NOT `URLSearchParams` — and the difference is not cosmetic.
     *
     * `URLSearchParams` serialises as a form body: a space becomes `+`, and `~` becomes `%7E`. The
     * app reads either back correctly, so nothing would have broken — but `?compare=told:a~b` is
     * published as `told%3Aa~b` on every page in the library today, and switching would have
     * rewritten every one of the 52,406 links to a different string for no reason, invisibly, with
     * every test still passing. All eleven call sites this replaced used `encodeURIComponent`; this
     * produces the same bytes they did.
     */
    parts.push(`${key}=${encodeURIComponent(value.trim())}`);
  }
  return `${base}/${parts.length === 0 ? '' : `?${parts.join('&')}`}`;
}
